import React, { useState } from "react";
import { ClockClockwise, CaretDown, CaretUp, Trash } from "@phosphor-icons/react";

/**
 * Historial de sesiones de rutinas. Muestra un accordion collapsible con
 * cada sesión completada, permitiendo expandir para ver el detalle de sets.
 * Admin puede borrar sesiones. Extraído de RoutinesTab.jsx — self-contained.
 */
export default function SessionHistory({ history, showHistory, setShowHistory, isAdmin, onDelete }) {
  const [expanded, setExpanded] = useState(null);
  const fmtDuration = (s) => {
    if (!s || s < 0) return "—";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}m ${sec}s`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  // Consistency: days with at least 1 session in last 30 days
  const last30 = history.filter((h) => {
    const d = new Date(h.started_at);
    return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24) <= 30;
  });
  const uniqueDays = new Set(last30.map((h) => (h.started_at || "").slice(0, 10))).size;

  return (
    <div className="mt-10">
      <button
        onClick={() => setShowHistory(!showHistory)}
        className="w-full card-base p-5 flex items-center justify-between hover:border-[#FF3B30] transition-colors"
        data-testid="toggle-history"
      >
        <div className="flex items-center gap-3">
          <ClockClockwise size={22} weight="bold" className="text-primary-app" />
          <div className="text-left">
            <div className="font-display font-black uppercase text-xl tracking-tight">Historial de Sesiones</div>
            <div className="text-xs text-muted uppercase tracking-widest mt-1">
              {history.length} sesiones · {uniqueDays} días entrenados en los últimos 30
            </div>
          </div>
        </div>
        {showHistory ? <CaretUp size={20} weight="bold" className="text-muted" /> : <CaretDown size={20} weight="bold" className="text-muted" />}
      </button>

      {showHistory && (
        <div className="mt-4 space-y-3" data-testid="history-list">
          {history.length === 0 ? (
            <div className="card-base p-8 text-center text-muted text-sm">
              Aún no hay sesiones completadas. Iniciá una rutina para empezar a registrar tu historial.
            </div>
          ) : (
            history.map((s) => (
              <div key={s.session_id} className="card-base p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${
                        s.status === "completed" ? "bg-[#32D74B] text-black" : s.status === "aborted" ? "bg-[#FFD60A] text-black" : "bg-[#262626] text-muted"
                      }`}>
                        {s.status === "completed" ? "Completada" : s.status === "aborted" ? "Interrumpida" : "En curso"}
                      </span>
                      <div className="font-display font-bold uppercase text-base truncate">{s.routine_name}</div>
                    </div>
                    <div className="text-xs text-muted">
                      Inicio: {fmtDate(s.started_at)} · Fin: {fmtDate(s.ended_at)} · Duración: <span className="text-white font-semibold">{fmtDuration(s.duration_sec)}</span> · {(s.sets || []).length} series
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setExpanded(expanded === s.session_id ? null : s.session_id)} className="btn-ghost text-xs uppercase tracking-widest" data-testid={`expand-session-${s.session_id}`}>
                      {expanded === s.session_id ? "Ocultar" : "Detalle"}
                    </button>
                    {isAdmin && (
                      <button onClick={() => onDelete(s.session_id)} className="btn-secondary !py-1.5 !px-2 hover:!border-[#FF3B30]" data-testid={`delete-session-${s.session_id}`}>
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>

                {expanded === s.session_id && (s.sets || []).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-app overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left">
                          <th className="py-2 pr-3 uppercase tracking-widest text-muted">Ejercicio</th>
                          <th className="py-2 px-3 uppercase tracking-widest text-muted">Serie</th>
                          <th className="py-2 px-3 uppercase tracking-widest text-muted">Peso</th>
                          <th className="py-2 px-3 uppercase tracking-widest text-muted">Reps</th>
                          <th className="py-2 px-3 uppercase tracking-widest text-muted">RPE</th>
                          <th className="py-2 px-3 uppercase tracking-widest text-muted">Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.sets.map((st, i) => (
                          <tr key={i} className="border-t border-app">
                            <td className="py-2 pr-3">{st.exercise_name}</td>
                            <td className="py-2 px-3">{st.set_number}</td>
                            <td className="py-2 px-3">{st.weight_kg} kg</td>
                            <td className="py-2 px-3">{st.reps}</td>
                            <td className="py-2 px-3 font-semibold" style={{ color: st.rpe >= 9 ? "#FF3B30" : st.rpe >= 7 ? "#FF9500" : st.rpe >= 4 ? "#FFD60A" : "#32D74B" }}>
                              {st.rpe ?? "—"}
                            </td>
                            <td className="py-2 px-3 text-muted">{st.notes || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
